/**
 * Button Component
 *
 * Design-system-compliant button with variants.
 * Adapts to light/dark via CSS variables.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { glassStyles } from '../../lib/design';
import type { CSSProperties } from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const sizeClasses = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
};

const variantClasses = {
  primary: 'bg-[#111111] hover:bg-[#333333] active:scale-[0.98] text-white focus:ring-[#1A1A1A]/40',
  secondary: 'bg-white/60 hover:bg-white/75 text-[var(--text-primary)] border border-[var(--border-default)] focus:ring-[#1A1A1A]/20',
  ghost: 'bg-transparent hover:bg-[#111111]/5 text-[#1A1A1A]',
  danger: 'bg-[#FF3B30] hover:brightness-110 active:scale-[0.98] text-white focus:ring-[#FF3B30]/40',
  glass: 'text-[var(--text-primary)] hover:bg-white/75',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      icon,
      iconPosition = 'left',
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseClasses = `
      inline-flex items-center justify-center gap-2
      font-medium rounded-xl
      transition-all duration-150
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const combinedClasses = `
      ${baseClasses}
      ${sizeClasses[size]}
      ${variantClasses[variant]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const glassStyle: CSSProperties | undefined = variant === 'glass'
      ? glassStyles.buttonSecondary
      : undefined;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={combinedClasses}
        style={{ ...glassStyle, ...style }}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {!loading && icon && iconPosition === 'left' && icon}
        {children}
        {!loading && icon && iconPosition === 'right' && icon}
      </button>
    );
  }
);

Button.displayName = 'Button';
